import React, { Component, ErrorInfo, ReactNode } from 'react'
import { Button, Result } from 'antd'
import { ReloadOutlined, HomeOutlined } from '@ant-design/icons'
import * as Sentry from '@sentry/react'
import { showFeedbackDialog } from '@/config/sentry.config'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
  isChunkError: boolean
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
    isChunkError: false
  }

  public static getDerivedStateFromError(error: Error): Partial<State> {
    // Check if it's a chunk loading error
    const isChunkError =
      error.message.includes('Failed to fetch dynamically imported module') ||
      error.message.includes('Loading chunk') ||
      error.message.includes('ChunkLoadError')

    return {
      hasError: true,
      error,
      isChunkError
    }
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo)

    this.setState({
      error,
      errorInfo
    })

    // Send error to Sentry with React component stack
    Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack
        }
      }
    })
  }

  private handleReload = () => {
    // For chunk errors, force a hard reload to get fresh chunks
    window.location.reload()
  }

  private handleGoHome = () => {
    window.location.href = '/'
  }

  private handleClearCache = async () => {
    try {
      // Unregister service workers
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations()
        await Promise.all(registrations.map((reg) => reg.unregister()))
      }

      // Clear cache storage
      if ('caches' in window) {
        const cacheNames = await caches.keys()
        await Promise.all(cacheNames.map((name) => caches.delete(name)))
      }

      // Clear local storage (optional - be careful with this)
      // localStorage.clear()

      // Reload the page
      window.location.reload()
    } catch (error) {
      console.error('Failed to clear cache:', error)
      // Fallback to simple reload
      window.location.reload()
    }
  }

  private handleReportFeedback = () => {
    // Show Sentry user feedback dialog
    showFeedbackDialog()
  }

  public render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback
      }

      // Chunk loading error - provide specific guidance
      if (this.state.isChunkError) {
        return (
          <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
            <Result
              status="500"
              title="Memuat Halaman Gagal"
              subTitle="Sepertinya ada versi baru aplikasi yang tersedia. Silakan muat ulang halaman untuk mendapatkan versi terbaru."
              extra={[
                <Button
                  type="primary"
                  size="large"
                  icon={<ReloadOutlined />}
                  onClick={this.handleReload}
                  key="reload"
                >
                  Muat Ulang Halaman
                </Button>,
                <Button
                  size="large"
                  onClick={this.handleClearCache}
                  key="clear"
                >
                  Hapus Cache & Muat Ulang
                </Button>
              ]}
            >
              <div className="text-sm text-gray-600">
                <p className="mb-2">
                  <strong>Tips Cepat:</strong>
                </p>
                <ul className="list-inside list-disc text-left">
                  <li>Tekan tombol &rdquo;Muat Ulang Halaman&rdquo; di atas</li>
                  <li>
                    Atau tekan{' '}
                    <kbd className="rounded bg-gray-200 px-1">Ctrl</kbd> +{' '}
                    <kbd className="rounded bg-gray-200 px-1">Shift</kbd> +{' '}
                    <kbd className="rounded bg-gray-200 px-1">R</kbd> untuk hard
                    reload
                  </li>
                  <li>
                    Jika masih bermasalah, klik &rdquo;Hapus Cache & Muat
                    Ulang&rdquo;
                  </li>
                </ul>
              </div>
            </Result>
          </div>
        )
      }

      // General error
      return (
        <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
          <Result
            status="error"
            title="Terjadi Kesalahan"
            subTitle="Maaf, terjadi kesalahan yang tidak terduga. Silakan coba lagi atau kembali ke beranda."
            extra={[
              <Button
                type="primary"
                size="large"
                icon={<HomeOutlined />}
                onClick={this.handleGoHome}
                key="home"
              >
                Kembali ke Beranda
              </Button>,
              <Button
                size="large"
                icon={<ReloadOutlined />}
                onClick={this.handleReload}
                key="reload"
              >
                Muat Ulang Halaman
              </Button>,
              <Button
                size="large"
                onClick={this.handleReportFeedback}
                key="feedback"
              >
                Laporkan Masalah
              </Button>
            ]}
          >
            {import.meta.env.DEV && this.state.error && (
              <div className="mt-4 rounded-lg bg-red-50 p-4 text-left">
                <p className="mb-2 font-semibold text-red-800">
                  Error Details (Development Only):
                </p>
                <pre className="overflow-auto text-xs text-red-700">
                  {this.state.error.toString()}
                </pre>
                {this.state.errorInfo && (
                  <details className="mt-2">
                    <summary className="cursor-pointer font-semibold text-red-800">
                      Component Stack
                    </summary>
                    <pre className="mt-2 overflow-auto text-xs text-red-700">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  </details>
                )}
              </div>
            )}
          </Result>
        </div>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary
